// The snapshot load: the one place that fills the store from the API.
//
// Until this file existed, Tasks.tsx and Reports.tsx read from
// `state.value.task` / `state.value.reportDefinition` and NOTHING ever wrote
// them. Both pages were finished, tested, and permanently empty — which is the
// worst shape a feature can be in, because every test is green and the owner
// sees nothing. Each page's header named the gap honestly and pointed here.
//
// INV-1 is why this is a SNAPSHOT at sign-in rather than a fetch per page:
// "navigation between pages = 0 network calls". This runs once when the
// session becomes mapped, and again only on an explicit refresh. Moving any of
// it into a page's mount would re-introduce F-44.
//
// Failures are per-slice and NAMED (INV-9): if work items fail to load, the
// work-item shelf says so and tasks still render. One dead endpoint must not
// blank the whole app, and a blank must never be mistaken for "nothing there".
import { authedGet } from './apiClient';
import { session } from './session';
import { clinicToday, clinicNow } from './clinicDay';
import {
  putEntityTable, persistResource, peekResource, clearResourceCache,
  hydrateFromMirror, hydrated, state, snapshotReadyResources, primeResource,
  resourcePendingFetch,
} from './store';
import { isCompanyDriveKey, refreshCompanyDriveLists, warmCompanyHr } from './companyDrive';
import { LOCATION_NAMES, LOCATION_ORDER } from './locations';
import { cachedFavoritesAreFor } from './favorites';
import { fetchDeskLayout, cachedDeskLayoutIsFor } from './deskLayout';
import { currentPeriodSet } from './taskPeriods';
import { signal } from '@preact/signals';
import { syncDataVersions, groupKeptThisVisit } from './dataVersions';
import { viewAsLocation } from './viewAs';
import { fetchCommunications } from './communications';

export interface TaskPeriod {
  id: number;
  period_label: string;
  period_start: string;
  period_end: string;
  closed: boolean;
}

/** What the snapshot loaded, and what it could not. Rendered, never swallowed. */
export interface SnapshotState {
  loading: boolean;
  loadedAt: string | null;
  period: TaskPeriod | null;
  faults: Array<{ slice: string; detail: string }>;
}

export const snapshot = signal<SnapshotState>({
  loading: false,
  loadedAt: null,
  period: null,
  faults: [],
});

/** Index an array of rows by id, the shape every page's store contract expects. */
function byId<T extends { id: number | string }>(rows: T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const row of rows) out[String(row.id)] = row;
  return out;
}

/**
 * The period a client should open on: the one containing today, else the most
 * recent one that has started. Chosen from the server's list rather than
 * computed from a label format — `period_label` is free text, and parsing it
 * would be the fuzzy matching standing rule 8 forbids.
 */
/**
 * A period bound as a plain YYYY-MM-DD string.
 *
 * `period_start` / `period_end` are `date` columns and carry no time and no
 * zone; the API now returns them as text for exactly that reason. Comparing
 * them as INSTANTS is what F-62 was (the API answering with the wrong day west
 * of UTC), and it breaks hardest on a one-day daily period: rendered as an
 * instant, the window is a single moment and "today" is inside it only at
 * midnight. Older values may still arrive as timestamps, so those are reduced
 * to their date part rather than rejected.
 */
function asDateString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  // A date column rendered as midnight UTC still names that one day.
  const midnight = /^(\d{4}-\d{2}-\d{2})T0+(?::0+)+(?:\.0+)?Z$/.exec(value);
  if (midnight) return midnight[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return clinicToday(d);
}

/** Today at the CLINICS, not in the browser's timezone — see lib/clinicDay.ts.
 * This decides which period the whole app opens on. */
function todayString(now: Date): string {
  return clinicToday(now);
}

export function pickCurrentPeriod(periods: TaskPeriod[], now: Date): TaskPeriod | null {
  if (!periods.length) return null;
  const today = todayString(now);
  const containing = periods.find((p) => {
    const start = asDateString(p.period_start);
    const end = asDateString(p.period_end);
    return start && end && start <= today && today <= end;
  });
  if (containing) return containing;
  const started = periods
    .filter((p) => asDateString(p.period_start) && asDateString(p.period_start) <= today)
    .sort((a, b) => (asDateString(b.period_start) < asDateString(a.period_start) ? -1 : 1));
  return started[0] ?? periods[0];
}

/**
 * The DISTINCT period labels a client should open with — one per cadence.
 *
 * `pickCurrentPeriod` returns a single period, which was right while weekly
 * was the only cadence in production. Since the daily and monthly definitions
 * were imported (2026-08-15) there are three current periods at any moment —
 * today, this ISO week, this calendar month — and loading one of them means
 * the other two cadences are invisible: a manager would open Tasks and see 13
 * daily tasks with no sign that 19 weekly and 22 monthly ones exist.
 *
 * Labels, not periods: `task_period` rows are per-DEFINITION (007), so this
 * week is 19 separate rows sharing one label, and `GET /api/v1/tasks?period=`
 * filters by that label. Deduplicating here is what keeps this at three
 * requests instead of fifty-four.
 *
 * Falls back to `pickCurrentPeriod`'s single answer when nothing contains
 * today — a system between periods still shows its most recent one rather
 * than an empty page.
 */
export function currentPeriodLabels(periods: TaskPeriod[], now: Date): string[] {
  const today = todayString(now);
  const labels: string[] = [];
  for (const p of periods) {
    const start = asDateString(p.period_start);
    const end = asDateString(p.period_end);
    if (!start || !end) continue;
    if (start <= today && today <= end && !labels.includes(p.period_label)) labels.push(p.period_label);
  }
  if (labels.length) return labels;
  const fallback = pickCurrentPeriod(periods, now);
  return fallback ? [fallback.period_label] : [];
}

/** Load one slice, recording a NAMED fault instead of throwing the whole load away. */
async function slice(
  name: string,
  faults: Array<{ slice: string; detail: string }>,
  fn: () => Promise<void>
): Promise<void> {
  try {
    await fn();
  } catch (err) {
    faults.push({ slice: name, detail: err instanceof Error ? err.message : String(err) });
  }
}

export interface StandingRow {
  task_definition_id: number;
  location_code: string;
  definition_name?: string;
  due_rule?: string;
  cadence?: string;
  status?: string;
  id?: number | null;
  [key: string]: unknown;
}

function standingKeyOf(row: { task_definition_id: number; location_code: string }): string {
  return `${row.task_definition_id}:${row.location_code}`;
}

/** Active standing rows only — retired definitions never come back. Drive is
 * skipped here; weekly file links are a later slice. */
async function fetchStandingMembership(): Promise<Record<string, StandingRow>> {
  const perCadence = await Promise.all(
    (['DAILY', 'WEEKLY', 'MONTHLY', 'MANAGER'] as const).map((cadence) => authedGet<{
      tasks: StandingRow[];
    }>(`/api/v1/task-definitions?cadence=${cadence}&drive=0`)),
  );
  const rows: Record<string, StandingRow> = {};
  for (const r of perCadence) {
    for (const t of r.tasks || []) {
      rows[standingKeyOf(t)] = t;
    }
  }
  return rows;
}

/**
 * Keep task names / due-by / instructions from the saved standing list, and
 * copy only the live ticks (who marked done, this period's dates) from a
 * fresh `/api/v1/tasks` response. Owner, 2026-08-19: the list itself is as
 * static as a table heading.
 */
export function overlayStandingStatus(
  standing: Record<string, StandingRow>,
  tasks: StandingRow[],
): Record<string, StandingRow> {
  const liveByKey = new Map<string, StandingRow>();
  for (const t of tasks) liveByKey.set(standingKeyOf(t), t);
  const out: Record<string, StandingRow> = {};
  for (const [key, row] of Object.entries(standing)) {
    const live = liveByKey.get(key);
    if (!live) {
      out[key] = {
        ...row,
        id: null,
        status: 'NOT_STARTED',
        completed_by: null,
        completed_at: null,
        verified_by: null,
        verified_at: null,
        sent_back_reason: null,
        report_url: null,
        notes: null,
      };
      continue;
    }
    const next: StandingRow = {
      ...row,
      id: live.id,
      status: live.status,
      completed_by: live.completed_by,
      completed_at: live.completed_at,
      verified_by: live.verified_by,
      verified_at: live.verified_at,
      sent_back_reason: live.sent_back_reason,
      report_url: live.report_url ?? row.report_url,
      attachment_url: live.attachment_url ?? row.attachment_url,
      attachment_name: live.attachment_name ?? row.attachment_name,
      notes: live.notes,
      updated_at: live.updated_at,
      task_period_id: live.task_period_id,
      period_label: live.period_label,
      period_start: live.period_start,
      period_end: live.period_end,
      period_closed: live.period_closed,
    };
    out[key] = next;
  }
  return out;
}

/** Weekly Drive links can appear after first load (a file lands in the
 * folder). Copy only those two fields; never a saved attach. */
export function overlayWeeklyAttachments(
  standing: Record<string, StandingRow>,
  weekly: StandingRow[],
): Record<string, StandingRow> {
  const out: Record<string, StandingRow> = { ...standing };
  for (const t of weekly) {
    const key = standingKeyOf(t);
    const row = out[key];
    if (!row) continue;
    out[key] = {
      ...row,
      attachment_url: t.attachment_url ?? null,
      attachment_name: t.attachment_name ?? null,
    };
  }
  return out;
}

function standingIsPopulated(): boolean {
  return Object.keys((state.value.standing ?? {}) as Record<string, unknown>).length > 0;
}

function tableHeld(entityType: string): boolean {
  return state.value[entityType] != null;
}

function tableHasRows(entityType: string): boolean {
  return Object.keys((state.value[entityType] ?? {}) as Record<string, unknown>).length > 0;
}

/** Pull the Deposits year grid + closed batches. Empty IndexedDB must not
 * count as "already loaded" — that is how Lake Mary stayed blank after the
 * Excel import (same trap as the people roster). */
let depositReconEpoch = 0;
export async function refreshDepositRecon(): Promise<void> {
  const epoch = ++depositReconEpoch;
  const [recons, batches] = await Promise.all([
    authedGet<{ depositRecons: Array<{ id: number }> }>('/api/v1/money/deposit-recons?from=2026-01-01&to=2026-12-31'),
    authedGet<{ depositBatches: Array<{ id: number }> }>('/api/v1/money/deposit-batches?from=2026-01-01&to=2026-12-31'),
  ]);
  if (epoch !== depositReconEpoch) return;
  await putEntityTable('depositRecon', byId(recons.depositRecons || []));
  await putEntityTable('depositBatch', byId(batches.depositBatches || []));
}

/** Money history opens on the last 90 clinic days (Cursor review #61, owner
 * 2026-10-15: "by default show only the last 90 days, with a Show older
 * button"). Applies to the five plain lists below — deposits, drawer counts,
 * petty cash, other cash, chargebacks. Nothing in the client totals them (the
 * per-clinic totals are the server's GET /money/summary). The Deposits year
 * grid (refreshDepositRecon) is NOT trimmed: its year-to-date box and its
 * open-days / last-closed-batch logic need the whole year. */
export const MONEY_HISTORY_DAYS = 90;

/** Set by "Show older": every later load of the lists (a delete's refresh,
 * the owner's Refresh) keeps asking for everything until the page reloads. */
export const moneyShowOlder = signal(false);

/** The first clinic (Eastern) day inside the window, YYYY-MM-DD. */
export function moneyHistoryFrom(now: Date = new Date()): string {
  // Calendar arithmetic on the clinic's wall-clock date (clinicNow), so a
  // daylight-saving change never moves the answer by a day.
  const d = clinicNow(now);
  d.setDate(d.getDate() - MONEY_HISTORY_DAYS);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export async function loadMoneyLists(now: Date = new Date()): Promise<void> {
  const q = moneyShowOlder.value ? '' : `?from=${moneyHistoryFrom(now)}`;
  const [deposits, drawers, petty, otherCash, chargebacks] = await Promise.all([
    authedGet<{ deposits: Array<{ id: number }> }>(`/api/v1/money/deposits${q}`),
    authedGet<{ drawerCounts: Array<{ id: number }> }>(`/api/v1/money/drawer-counts${q}`),
    authedGet<{ pettyCash: Array<{ id: number }> }>(`/api/v1/money/petty-cash${q}`),
    authedGet<{ otherCashItems: Array<{ id: number }> }>(`/api/v1/money/other-cash${q}`),
    authedGet<{ chargebacks: Array<{ id: number }> }>(`/api/v1/money/chargebacks${q}`),
  ]);
  await putEntityTable('deposit', byId(deposits.deposits || []));
  await putEntityTable('drawerCount', byId(drawers.drawerCounts || []));
  await putEntityTable('pettyCash', byId(petty.pettyCash || []));
  await putEntityTable('otherCashItem', byId(otherCash.otherCashItems || []));
  await putEntityTable('chargeback', byId(chargebacks.chargebacks || []));
}

/** The Money page's "Show older" button. Throws on failure; the caller names it. */
export async function showOlderMoney(): Promise<void> {
  moneyShowOlder.value = true;
  try {
    await loadMoneyLists();
  } catch (err) {
    moneyShowOlder.value = false;
    throw err;
  }
}

/** A saved "not set up" Drive logo must not block a later fetch — that is
 * how every manager logo stayed grey after the clinic folders were added. */
function driveShortcutReady(): boolean {
  const me = (state.value.driveShortcut as { me?: { configured?: boolean; url?: string } } | undefined)?.me;
  return Boolean(me?.configured && me.url);
}

async function ensureClinicList(): Promise<void> {
  if (tableHeld('location') && Object.keys(state.value.location ?? {}).length > 0) return;
  const byCode: Record<string, unknown> = {};
  for (const code of LOCATION_ORDER) {
    byCode[code] = { code, name: LOCATION_NAMES[code], hue: null };
  }
  await putEntityTable('location', byCode);
}

function mergeComputedPeriods(now: Date): TaskPeriod[] {
  const computed = currentPeriodSet(now).map((p, i) => ({
    id: i + 1,
    period_label: p.periodLabel,
    period_start: p.periodStart,
    period_end: p.periodEnd,
    closed: false,
  }));
  const cached = peekResource<{ periods: TaskPeriod[] }>('task-periods')?.periods ?? [];
  const byLabel = new Map<string, TaskPeriod>();
  for (const p of cached) byLabel.set(p.period_label, p);
  for (const p of computed) byLabel.set(p.period_label, p);
  return [...byLabel.values()].sort((a, b) => b.period_start.localeCompare(a.period_start));
}

// The clinic day (YYYY-MM-DD) the standing list's ticks were last loaded for.
// `null` until the first load. Read by refreshTasksIfNewDay.
let ticksLoadedForDay: string | null = null;

// The Eastern day the saved ticks belong to, saved with them. The server's
// `tasks` fingerprint says nothing moved, but the ticks are per-DAY: last
// night's DONE marks must never be reused as today's (idea #32, Lake Mary).
// Listed in lib/dataVersions.ts's `tasks` group so it travels with them.
const TICKS_DAY_KEY = 'task-ticks-day';

/**
 * Today's ticks and the standing list, from the server, merged into the store.
 *
 * Owner, 2026-08-17: "every day at 12:01 est it should reset the table…
 * weekly should be every Sunday at 12:01 Midnight so Monday… Monthly on the
 * 12:01 of the 1st of every month." The reset is not a job. The current
 * period is computed from the clock (lib/taskPeriods.ts) and the server is
 * asked for THAT period's rows every time this runs; last visit's copy paints
 * first (hydrateFromMirror) and is replaced the moment this answers.
 *
 * Why this ALWAYS asks (2026-09-14): from the 2026-09-05 release the load
 * skipped this whenever a saved standing list existed. A manager's own DONE
 * ticks — written into the saved copy by Tasks.tsx setStatus — then stayed on
 * screen day after day, and three clinics reported "the task list stopped
 * resetting" (ideas #22, #24, #25; last clean day 09-07, the first visit after
 * that release). The server had rolled the day correctly throughout. Names,
 * due-by and instructions come from the standing list; the ticks come from
 * this period's rows; `overlayStandingStatus` joins the two.
 */
async function loadTasksAndStanding(now: Date): Promise<void> {
  const labels = [...new Set(currentPeriodSet(now).map((p) => p.periodLabel))];
  const [perLabel, membership] = await Promise.all([
    Promise.all(labels.map((label) => authedGet<{ tasks: StandingRow[] }>(
      `/api/v1/tasks?period=${encodeURIComponent(label)}`,
    ))),
    fetchStandingMembership(),
  ]);
  const taskRows = perLabel.flatMap((r) => r.tasks || []);
  await putEntityTable('task', byId(taskRows.filter((t) => t.id != null) as Array<{ id: number }>));
  await putEntityTable('standing', overlayStandingStatus(membership, taskRows));
  ticksLoadedForDay = clinicToday(now);
  // Remember WHICH Eastern day these ticks belong to, next to the ticks, so a
  // later visit can tell last night's copy from today's (idea #32).
  await persistResource(TICKS_DAY_KEY, { day: ticksLoadedForDay });
}

/**
 * Fill the store from the API. Called once when the session becomes mapped.
 * Never called on navigation. Last visit's copy paints first; the task list
 * and today's ticks are always asked for again (see loadTasksAndStanding).
 * The other slices are not refetched unless Refresh or a write changed them.
 */
export async function loadSnapshot(
  now: Date = new Date(),
  { force = false }: { force?: boolean } = {},
): Promise<void> {
  if (!hydrated.value) await hydrateFromMirror();
  snapshot.value = { ...snapshot.value, loading: true, faults: [] };
  // Keep what has not changed since the last visit, drop what has (owner,
  // 2026-09-24 — lib/dataVersions.ts). Runs before the keep* checks below so
  // a kept group counts as already fetched this visit.
  await syncDataVersions(`${session.value.me?.email ?? ''}|${viewAsLocation.value ?? ''}`);
  const faults: Array<{ slice: string; detail: string }> = [];
  let period: TaskPeriod | null = null;
  // Whether a copy of the standing list from a previous visit is already on
  // screen — decides whether a failed task refresh is a fault or a quiet keep.
  const hadSavedStanding = standingIsPopulated();
  // N-090c (owner, 2026-09-08): these four used to read `peekResource(key)
  // !== null` — "ANY cached answer, skip the fetch" — which treats a copy
  // only ever mirrored in from a PREVIOUS session's IndexedDB snapshot
  // (`hydrateFromMirror`, marked accordingly) the same as one this session
  // actually asked the server for. That is what let the SPC panel keep
  // showing "Nothing to show right now" after `service-compliance` had rows
  // again: bootstrap saw a mirrored (however stale) answer already sitting
  // in the cache and never re-asked, and nothing else in this session was
  // going to either (My Desk's SPC panel only mounts the component that
  // WOULD ask — ComplianceAlerts — once it already believes there is
  // something to show; see panelCatalog.tsx's `spcAlertsHasContent`). These
  // four are exactly the alert-style slices a manager or director may go a
  // whole session without ever opening the page that would otherwise
  // revalidate them (Payroll, People, Service Compliance) — `!resourcePendingFetch`
  // asks the server again whenever the only copy on hand is not one THIS
  // session actually fetched, same as a hard Refresh would, without waiting
  // for a page visit that may never come.
  const keepPeople = !force && !resourcePendingFetch('people-roster');
  const keepMoney = !force && tableHeld('deposit') && (tableHasRows('depositRecon') || tableHasRows('depositBatch'));
  const keepFavorites = !force && cachedFavoritesAreFor(session.value.me?.email);
  const keepDeskLayout = !force && cachedDeskLayoutIsFor(session.value.me?.email);
  const keepDrive = !force && driveShortcutReady();
  const keepPayroll = !force && tableHeld('payPeriod');
  const keepReports = !force && tableHeld('reportDefinition');
  const keepWorkItems = !force && tableHeld('workItem');
  const keepHourRaises = !force && !resourcePendingFetch('hour-raises:v4');
  const keepNeedsHireDetails = !force && !resourcePendingFetch('people-needs-details:v1');
  const keepCompliance = !force && !resourcePendingFetch('service-compliance');
  // N-094 ("forward and file"): same "alert a manager/director may go a
  // whole session without revisiting" reasoning as the three above —
  // Communications and Intake are My Desk panels (CommunicationsPanel.tsx /
  // IntakePanel.tsx), and "License expiring" is merged into ComplianceAlerts.
  const keepCommunications = !force && !resourcePendingFetch('communications');
  const keepIntake = !force && !resourcePendingFetch('intake');
  const keepLicenses = !force && !resourcePendingFetch('company-licenses');
  // Tasks (owner, 2026-09-26): last visit's ticks and standing list stand
  // when the server's `tasks` fingerprint has not moved — lib/dataVersions.ts
  // confirmed 'task-periods' and left both tables in place. A new clinic day
  // still reloads through refreshTasksIfNewDay; a Refresh (force) always does.
  const keepTasks = !force && groupKeptThisVisit('tasks') && tableHeld('task') && tableHeld('standing')
    && peekResource<{ day: string }>(TICKS_DAY_KEY)?.day === clinicToday(now);
  const keepIdeas = !force && groupKeptThisVisit('desk') && !resourcePendingFetch('automation-ideas:v4');

  await ensureClinicList();

  await Promise.all([
    slice('tasks', faults, async () => {
      const periods = mergeComputedPeriods(now);
      await persistResource('task-periods', { periods });
      period = pickCurrentPeriod(periods, now);
      if (keepTasks) {
        // The copy on hand is today's truth (see keepTasks); only the
        // day watch needs to know which day it belongs to.
        ticksLoadedForDay = clinicToday(now);
        return;
      }
      try {
        await loadTasksAndStanding(now);
      } catch (err) {
        // Same rule as useResource's revalidation (owner, 2026-09-07): a
        // refresh that fails leaves last visit's copy standing, so the manager
        // keeps a working screen. Only a FIRST load, or an explicit Refresh,
        // faults out loud.
        if (!hadSavedStanding || force) throw err;
      }
    }),
  ]);

  // Tasks can paint now. People / money / Drive must not keep the board empty.
  snapshot.value = {
    loading: false,
    loadedAt: now.toISOString(),
    period,
    faults: [...faults],
  };

  // An assistant's session may reach the Tasks routes only (src/assistant_gate.js):
  // everything below this line would be refused, so it is not asked for.
  if (session.value.me?.role === 'ASSISTANT') {
    await slice('weekly attachments', faults, async () => {
      if (keepTasks || !standingIsPopulated()) return;
      const weekly = await authedGet<{ tasks: StandingRow[] }>('/api/v1/task-definitions?cadence=WEEKLY');
      const current = (state.value.standing ?? {}) as Record<string, StandingRow>;
      await putEntityTable('standing', overlayWeeklyAttachments(current, weekly.tasks || []));
    });
    snapshot.value = { loading: false, loadedAt: now.toISOString(), period, faults };
    return;
  }

  await Promise.all([
    slice('weekly attachments', faults, async () => {
      if (keepTasks) return; // the overlay is already in the kept standing table
      if (!standingIsPopulated()) return;
      try {
        const weekly = await authedGet<{ tasks: StandingRow[] }>(
          '/api/v1/task-definitions?cadence=WEEKLY',
        );
        const current = (state.value.standing ?? {}) as Record<string, StandingRow>;
        await putEntityTable('standing', overlayWeeklyAttachments(current, weekly.tasks || []));
      } catch (err) {
        // Drive can hang. A saved list stays on screen (same rule as the
        // tasks slice above); a first load or a Refresh says so.
        if (!hadSavedStanding || force) throw err;
      }
    }),

    slice('reports', faults, async () => {
      if (keepReports) return;
      const { reports, latestByLocation } = await authedGet<{
        reports: Array<{ id: number }>;
        latestByLocation?: Record<string, unknown>;
      }>('/api/v1/reports');
      await putEntityTable('reportDefinition', byId(reports || []));
      await putEntityTable('reportShelf', (latestByLocation ?? {}) as Record<string, unknown>);
    }),

    slice('people', faults, async () => {
      if (keepPeople) return;
      const data = await authedGet<{ people: unknown[]; locations: string[] }>('/api/people');
      await persistResource('people-roster', data);
    }),

    slice('favorites', faults, async () => {
      if (keepFavorites) return;
      const data = await authedGet<{ favorites: unknown[]; catalog: unknown[] }>('/api/v1/favorites');
      await persistResource('favorites', data);
    }),

    // My Desk's saved arrangement (N-090) — primed here so Home.tsx never
    // pays a network call on ordinary navigation, same as every other
    // per-user resource on this list.
    slice('desk layout', faults, async () => {
      if (keepDeskLayout) return;
      await fetchDeskLayout();
    }),

    slice('drive', faults, async () => {
      if (keepDrive) return;
      const shortcut = await authedGet<{ configured: boolean }>('/api/v1/my-drive');
      await putEntityTable('driveShortcut', { me: shortcut });
    }),

    // Tasks > Alerts tab badge (unreviewed count). Best effort: a failure here
    // only means no badge, so it is not recorded as a fault.
    (async () => {
      try {
        await persistResource('task-alerts-summary', await authedGet('/api/v1/task-alerts/summary'));
      } catch { /* no badge */ }
    })(),

    slice('work items', faults, async () => {
      if (keepWorkItems) return;
      const { workItems } = await authedGet<{ workItems: Array<{ id: number }> }>('/api/v1/work-items');
      await putEntityTable('workItem', byId(workItems || []));
    }),

    slice('ideas', faults, async () => {
      if (keepIdeas) return;
      const data = await authedGet<{ ideas: unknown[]; locations: string[] }>('/api/v1/automation-ideas');
      await persistResource('automation-ideas:v4', data);
    }),

    slice('hour raises', faults, async () => {
      if (keepHourRaises) return;
      const data = await authedGet<{ raises: unknown[]; following: unknown; raiseAt: number }>(
        '/api/v1/hour-raises',
      );
      await persistResource('hour-raises:v4', data);
    }),

    // N-084 (Request Ledger, owner 2026-09-08): "N people need hire details"
    // on Home/Desk, OWNER and DIRECTOR (Candace added 2026-10-06). The server
    // route is OWNER/DIRECTOR (unlike hour-raises, which is open to any
    // signed-in role and scopes to the viewer's clinics) — primed only for
    // those two here so a manager session never draws a 403 fault for a slice
    // their own account cannot read (same skip shape the payroll slice below
    // uses for MANAGER).
    slice('people needing hire details', faults, async () => {
      if (keepNeedsHireDetails) return;
      // Same two roles as NeedsHireDetailsAlert.tsx's canSeeNewHires (kept
      // inline so this lib file does not import a component module).
      const hireDetailsRole = session.value.me?.role;
      if (hireDetailsRole !== 'OWNER' && hireDetailsRole !== 'DIRECTOR') {
        await persistResource('people-needs-details:v1', { people: [] });
        return;
      }
      const data = await authedGet<{ people: unknown[] }>('/api/v1/people/needs-details');
      await persistResource('people-needs-details:v1', data);
    }),

    slice('service compliance', faults, async () => {
      if (keepCompliance) return;
      const data = await authedGet<{ rows: unknown[]; alertDays: number }>(
        '/api/v1/company/service-compliance',
      );
      await persistResource('service-compliance', data);
    }),

    slice('company licenses', faults, async () => {
      if (keepLicenses) return;
      const data = await authedGet<{ rows: unknown[]; alertDays: number }>('/api/v1/company/licenses');
      await persistResource('company-licenses', data);
    }),

    slice('communications', faults, async () => {
      if (keepCommunications) return;
      // The last 30 days plus anything still unread or must-read (Cursor
      // review #61); "Show older" on Company → Communications loads the rest.
      const data = await fetchCommunications();
      await persistResource('communications', data);
    }),

    // OWNER/DIRECTOR only server-side (a MANAGER's GET 403s) — skip the
    // network entirely for anyone else and persist an empty answer, same
    // shape as 'people needing hire details' above.
    slice('intake', faults, async () => {
      if (keepIntake) return;
      if (session.value.me?.role !== 'OWNER' && session.value.me?.role !== 'DIRECTOR') {
        await persistResource('intake', { items: [] });
        return;
      }
      const data = await authedGet<{ items: unknown[] }>('/api/v1/intake');
      await persistResource('intake', data);
    }),

    slice('money', faults, async () => {
      if (keepMoney) return;
      await Promise.all([loadMoneyLists(now), refreshDepositRecon()]);
    }),

    (async () => {
      if (keepPayroll) return;
      if (session.value.me?.role === 'MANAGER') {
        await putEntityTable('payPeriod', {});
        await putEntityTable('payrollReview', {});
        await putEntityTable('payrollExtra', {});
        await putEntityTable('registerLine', {});
        return;
      }
      await slice('payroll', faults, async () => {
        const [periods, reviews, extras] = await Promise.all([
          authedGet<{ payPeriods: Array<{ id: number }> }>('/api/v1/payroll/periods'),
          authedGet<{ reviews: Array<{ id: number }> }>('/api/v1/payroll/reviews'),
          authedGet<{ extras: Array<{ id: number }> }>('/api/v1/payroll/extras'),
        ]);
        await putEntityTable('payPeriod', byId(periods.payPeriods || []));
        await putEntityTable('payrollReview', byId(reviews.reviews || []));
        await putEntityTable('payrollExtra', byId(extras.extras || []));
        // Register lines are NOT mirrored to the browser (N-091, 2026-09-08):
        // nothing in the client reads them while the native week view is off
        // (Payroll.tsx NATIVE_WEEK_VIEW = false), and the copy had grown to
        // ~1 MB per session. The server computes everything that needs them.
        await putEntityTable('registerLine', {});
      });
    })(),
  ]);

  snapshot.value = {
    loading: false,
    loadedAt: now.toISOString(),
    period,
    faults,
  };

  // NHP / HR Misc sit in Drive and barely change. Warm the saved list now
  // so Company → HR is already on screen instead of walking Drive on click.
  warmCompanyHr();
}

/** Owner Refresh — drop cached page reads and load the snapshot again.
 * Company Drive lists (NHP, Misc HR, Documents) stay on screen; they
 * update quietly afterward so Refresh does not make HR wait on Drive. */
export async function reloadEverything(): Promise<void> {
  const kept = snapshotReadyResources((key) => isCompanyDriveKey(key));
  clearResourceCache();
  for (const row of kept) primeResource(row.key, row.data);
  await loadSnapshot(new Date(), { force: true });
  refreshCompanyDriveLists(kept.map((row) => row.key));
}

/**
 * If the clinic's calendar day has changed since the ticks were loaded, load
 * them again. A front desk leaves the tab open overnight; it must show the new
 * day's list without anyone pressing Refresh (owner, 2026-08-17: the table
 * resets at 12:01 Eastern). Returns true when it reloaded. A failed reload
 * leaves the copy on screen and is tried again on the next check.
 */
export async function refreshTasksIfNewDay(now: Date = new Date()): Promise<boolean> {
  if (ticksLoadedForDay === null || snapshot.value.loading) return false;
  if (clinicToday(now) === ticksLoadedForDay) return false;
  try {
    const periods = mergeComputedPeriods(now);
    await persistResource('task-periods', { periods });
    await loadTasksAndStanding(now);
    snapshot.value = { ...snapshot.value, period: pickCurrentPeriod(periods, now) };
    return true;
  } catch {
    return false;
  }
}

let stopDayWatch: (() => void) | null = null;

/** Check for a new clinic day whenever the tab comes back into view, and once
 * a minute while it stays open. Installed once; a second call is a no-op. */
export function watchClinicDay(everyMs = 60_000): void {
  if (stopDayWatch) return;
  const check = (): void => { void refreshTasksIfNewDay(new Date()); };
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  const timer = setInterval(check, everyMs);
  stopDayWatch = () => {
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('focus', check);
    clearInterval(timer);
  };
}

/** Sign-out (and tests): stop watching and forget which day was loaded. */
export function stopClinicDayWatch(): void {
  if (stopDayWatch) stopDayWatch();
  stopDayWatch = null;
  ticksLoadedForDay = null;
}

/**
 * This period's whole task list, for every clinic: each standing task with its
 * tick overlaid (NOT_STARTED when nobody has touched it). `state.value.task`
 * holds ONLY the ticked rows — counting from it showed "Weekly 1" for a clinic
 * with 18 weekly tasks and hid untouched overdue tasks (found 2026-10-12).
 * Falls back to the ticked rows only before the list has loaded.
 */
export function currentTaskList<T = StandingRow>(): T[] {
  const standing = Object.values((state.value.standing ?? {}) as Record<string, T>);
  return standing.length ? standing : Object.values((state.value.task ?? {}) as Record<string, T>);
}
